import {
  IconBank,
  SequenceMenu,
  SettingsFormControl,
  SettingsFormControlIcon,
  useModalState,
  useNotifications,
  SimpleModal,
  Box
} from '@sequencehq/core-components'
import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNavigate } from 'react-router-dom'
import {
  accountDetailsTypeDescription,
  MerchantBankAccountDetails
} from './bankDetails.types'
import { toCurrencyName } from '@sequencehq/utils'

const getAccountNumberFromType = (bankDetails: MerchantBankAccountDetails) => {
  switch (bankDetails.value.type) {
    case 'IBAN':
      return bankDetails.value.iban
    case 'AUSTRALIAN':
      return `${bankDetails.value.australianDestination?.accountNumber} ${bankDetails.value.australianDestination?.legalName}`
    case 'CA_BANK_ACCOUNT':
      return `${bankDetails.value.canadianBankAccount?.accountNumber}`
    case 'CA_INTERNATIONAL':
      return `${bankDetails.value.canadianInternationalDestination?.accountNumber} ${bankDetails.value.canadianInternationalDestination?.legalName}`
    case 'UK_BANK_ACCOUNT':
      return `${bankDetails.value.ukBankAccount?.accountNumber} ${bankDetails.value.ukBankAccount?.sortCode}`
    case 'US_ACH':
      return `${bankDetails.value.usAchDestination?.accountNumber} ${bankDetails.value.usAchDestination?.accountName}`
    case 'US_WIRE':
      return `${bankDetails.value.usWireDestination?.accountNumber} ${bankDetails.value.usWireDestination?.accountName}`
    case 'SWEDISH_BANKGIRO':
      return `${bankDetails.value.swedishBankgiroDestination?.bankgiroNumber} ${bankDetails.value.swedishBankgiroDestination?.accountName}`
  }
}

export const BankDetailsCard = ({
  bankDetails
}: {
  bankDetails: MerchantBankAccountDetails
}) => {
  const navigate = useNavigate()
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <SettingsFormControl
        key={bankDetails.value.type}
        label={`${bankDetails.key} - ${toCurrencyName(bankDetails.key)}`}
        htmlFor={`bank-details-${bankDetails.key}`}
        helper={getAccountNumberFromType(bankDetails)}
        onClick={() => {
          navigate(
            `/settings/bank-details/${bankDetails.key}/${bankDetails.value.type}`
          )
        }}
        icon={
          <SettingsFormControlIcon>
            <IconBank />
          </SettingsFormControlIcon>
        }
      >
        <SequenceMenu
          variant="ghost"
          buttonSize="small"
          items={[
            {
              label: 'Edit',
              onClick: () => {
                navigate(`/settings/bank-details/${bankDetails.key}`)
              }
            },
            {
              label: 'Delete',
              style: { color: 'red' },
              onClick: e => {
                e.stopPropagation()
                openModal()
              }
            }
          ]}
        />
      </SettingsFormControl>
      <DeleteBankDetailsModal
        bankDetails={bankDetails}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  )
}

const DeleteBankDetailsModal = ({
  bankDetails,
  isOpen,
  onClose
}: {
  bankDetails: MerchantBankAccountDetails
  isOpen: boolean
  onClose: () => void
}) => {
  const { displayNotification } = useNotifications()

  const merchant = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  const mutator = useMutation(dashboard20240730Client.putMerchant, {
    onSuccess() {
      displayNotification('Bank details deleted', {
        type: 'success'
      })
    },
    onError() {
      displayNotification('Bank details failed to delete', {
        type: 'error'
      })
    }
  })

  return (
    <SimpleModal
      variant="dialog"
      data-testid="bankDetails.deleteDialog"
      isOpen={isOpen}
      onClose={onClose}
      title="Delete bank details"
      submitButtonText="Delete"
      submittingButtonText="Deleting"
      isDestructive
      disableSubmitOnEnter
      onSubmit={async () => {
        if (!merchant.data) return false

        const newBankAccountDetails = merchant.data.bankAccountDetails.filter(
          details => {
            return !(
              details.key === bankDetails.key &&
              details.value.type === bankDetails.value.type
            )
          }
        )

        try {
          await mutator.mutateAsync({
            id: merchant.data.id,
            body: {
              address: merchant.data.address,
              email: merchant.data.email,
              legalCompanyName: merchant.data.legalCompanyName,
              bankAccountDetails: newBankAccountDetails
            }
          })
        } catch {
          return false
        }
        return true
      }}
    >
      Are you sure? This will permanently delete the{' '}
      <b>
        {bankDetails.key} -{' '}
        {accountDetailsTypeDescription[bankDetails.value.type]}
      </b>{' '}
      bank details.
    </SimpleModal>
  )
}
